import { ShoppingCart, Trash2 } from "lucide-react";
import Button from "../../../../../components/EcommerceActionButton/Button";
import { Label } from "../../../../../components/Label";
import { formatNumberToPrice } from "../../../../../helpers/priceFormatting";
import styles from "../QuoteForm.module.scss";

interface QuoteAccessoriesSectionProps {
  accessories: QuoteProduct[];
  autoAccessories: QuoteProduct[];
  totalM2: number;
  onAddAccessory: () => void;
  onRemoveAccessory: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onUpdateDiscount: (id: string, discount: number) => void;
  maxDiscountPercent: number;
}

export function QuoteAccessoriesSection({
  accessories,
  autoAccessories,
  totalM2,
  onAddAccessory,
  onRemoveAccessory,
  onUpdateQuantity,
  onUpdateDiscount,
  maxDiscountPercent,
}: QuoteAccessoriesSectionProps) {
  return (
    <>
      {/* Calculated Accessories Information */}
      {autoAccessories.length > 0 && (
        <div className={styles.recommendationBox}>
          <div className={styles.recommendationHeader}>
            <div className={styles.recommendationIcon}>
              <ShoppingCart />
            </div>
            <div className={styles.recommendationTitleBlock}>
              <Label className={styles.recommendationTitle}>
                Accesorios calculados
              </Label>
              <p className={styles.recommendationSubtitle}>
                Basado en {totalM2} m² totales de productos seleccionados
              </p>
            </div>
          </div>
          <div className={styles.recommendationContent}>
            {autoAccessories.map((accessory) => (
              <div className={styles.recommendationRow} key={accessory.id}>
                <span className={styles.recommendationLabel}>
                  {accessory.name}:
                </span>
                <span className={styles.recommendationValue}>
                  {accessory.quantity} {accessory.unit || "unid"}
                </span>
              </div>
            ))}
          </div>
          <p className={styles.recommendationNote}>
            <span className={styles.noteIcon}>ℹ️</span>
            <span>
              Estos accesorios se agregan automáticamente. Puedes ajustar
              cantidades en la sección de accesorios si lo necesitas.
            </span>
          </p>
        </div>
      )}

      {/* Accessories */}
      <div className={styles.itemsSection}>
        <div className={styles.itemsHeader}>
          <Label className={styles.label}>Accesorios</Label>
          <Button
            color="primary"
            shape="little-rounded"
            icon={ShoppingCart}
            onClick={onAddAccessory}
            className={styles.compactButton}
          >
            Agregar Accesorios
          </Button>
        </div>
        <div className={styles.itemsList}>
          {accessories.map((accessory) => (
            <div key={accessory.id} className={styles.itemCard}>
              <div className={styles.itemHeader}>
                <div className={styles.itemInfo}>
                  <div className={styles.itemName}>{accessory.name}</div>
                  <div className={styles.itemPrice}>
                    {formatNumberToPrice(accessory.price)} ×{" "}
                    {accessory.quantity}
                  </div>
                  <div className={styles.discountRow}>
                    <span className={styles.discountLabel}>Descuento %</span>
                    <input
                      type="number"
                      min="0"
                      max={maxDiscountPercent}
                      value={accessory.discountPercent ?? 0}
                      onChange={(e) =>
                        onUpdateDiscount(
                          accessory.id,
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={styles.discountInput}
                    />
                  </div>
                </div>
                <input
                  type="number"
                  min="1"
                  value={accessory.quantity}
                  onChange={(e) =>
                    onUpdateQuantity(
                      accessory.id,
                      parseInt(e.target.value) || 1
                    )
                  }
                  className={styles.quantityInput}
                />
                <div className={styles.itemTotal}>
                  {formatNumberToPrice(
                    accessory.price *
                      accessory.quantity *
                      (1 -
                        Math.min(
                          maxDiscountPercent,
                          Math.max(0, accessory.discountPercent || 0)
                        ) /
                          100)
                  )}
                  {accessory.discountPercent ? (
                    <span className={styles.itemTotalOriginal}>
                      {formatNumberToPrice(
                        accessory.price * accessory.quantity
                      )}
                    </span>
                  ) : null}
                </div>
                <Button
                  color="outlined"
                  shape="little-rounded"
                  size="small"
                  icon={Trash2}
                  onClick={() => onRemoveAccessory(accessory.id)}
                  className={styles.iconButton}
                >
                  Quitar
                </Button>
              </div>
            </div>
          ))}
          {accessories.length === 0 && (
            <p className={styles.emptyMessage}>No hay accesorios agregados</p>
          )}
        </div>
      </div>
    </>
  );
}
